import { useCallback, useMemo, useState, type ReactNode } from "react";
import {
	initialServiceRequestData,
	initialWizardData,
	type PendingHirePayload,
	type RegistrationWizardData,
	type ServiceRegistrationWizardContextValue,
	ServiceRegistrationWizardContext,
} from "./service-registration-store";
import type { FormState } from "../core/types/tsx/pages/ServiceRequestWizard.types";

const areStringArraysEqual = (a: string[], b: string[]) => {
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i += 1) {
		if (a[i] !== b[i]) return false;
	}
	return true;
};

const isSameFormState = (current: FormState, next: FormState) => {
	return (
		current.urgency === next.urgency &&
		current.category === next.category &&
		current.categoryId === next.categoryId &&
		current.location.nombreDireccion === next.location.nombreDireccion &&
		current.location.direccion === next.location.direccion &&
		current.location.referencias === next.location.referencias &&
		current.schedule.date === next.schedule.date &&
		current.schedule.time === next.schedule.time &&
		current.schedule.period === next.schedule.period &&
		areStringArraysEqual(current.subcategory, next.subcategory)
	);
};

export function ServiceRegistrationWizardProvider({ children }: { children: ReactNode }) {
	const [wizardData, setWizardData] =
		useState<RegistrationWizardData>(initialWizardData);
	const [serviceRequestData, setServiceRequestDataState] = useState<FormState>(
		initialServiceRequestData,
	);
	const [pendingHirePayload, setPendingHirePayload] =
		useState<PendingHirePayload | null>(null);

	const updateWizardData = useCallback((patch: Partial<RegistrationWizardData>) => {
		setWizardData((prev) => ({ ...prev, ...patch }));
	}, []);

	const resetWizardData = useCallback(() => {
		setWizardData(initialWizardData);
	}, []);

	const setServiceRequestData = useCallback((next: FormState) => {
		setServiceRequestDataState((prev) =>
			isSameFormState(prev, next) ? prev : next,
		);
	}, []);

	const updateServiceRequestData = useCallback((patch: Partial<FormState>) => {
		setServiceRequestDataState((prev) => {
			const next: FormState = {
				...prev,
				...patch,
				location: patch.location
					? { ...prev.location, ...patch.location }
					: prev.location,
				schedule: patch.schedule
					? { ...prev.schedule, ...patch.schedule }
					: prev.schedule,
				subcategory: patch.subcategory ?? prev.subcategory,
			};

			return isSameFormState(prev, next) ? prev : next;
		});
	}, []);

	const resetServiceRequestData = useCallback(() => {
		setServiceRequestDataState(initialServiceRequestData);
	}, []);

	const isWizardComplete = useMemo(() => {
		return (
			wizardData.acceptedTerms &&
			wizardData.paymentMethod.trim().length > 0 &&
			wizardData.paymentReference.trim().length >= 6
		);
	}, [wizardData.acceptedTerms, wizardData.paymentMethod, wizardData.paymentReference]);

	const isServiceRequestComplete = useMemo(() => {
		return Boolean(
			serviceRequestData.urgency &&
			serviceRequestData.location.nombreDireccion.trim() &&
			serviceRequestData.location.direccion.trim() &&
			serviceRequestData.schedule.date.trim() &&
			serviceRequestData.schedule.time.trim() &&
			serviceRequestData.categoryId &&
			serviceRequestData.subcategory.length > 0,
		);
	}, [serviceRequestData]);

	const value = useMemo<ServiceRegistrationWizardContextValue>(
		() => ({
			wizardData,
			updateWizardData,
			resetWizardData,
			isWizardComplete,
			serviceRequestData,
			setServiceRequestData,
			updateServiceRequestData,
			resetServiceRequestData,
			isServiceRequestComplete,
			pendingHirePayload,
			setPendingHirePayload,
		}),
		[
			isServiceRequestComplete,
			isWizardComplete,
			pendingHirePayload,
			resetServiceRequestData,
			resetWizardData,
			serviceRequestData,
			setServiceRequestData,
			updateServiceRequestData,
			updateWizardData,
			wizardData,
		],
	);

	return (
		<ServiceRegistrationWizardContext.Provider value={value}>
			{children}
		</ServiceRegistrationWizardContext.Provider>
	);
}
